import { forwardRef, useEffect, useMemo, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { MDBRow, MDBCol } from "mdbreact";

import "./FeuD5QuaysideAndAnchorage.scss";

import { Button, Label, TextArea } from "abs-ui-components";

import { FuelEuDCommonTableColumns } from "../../FeuConstants";
import FormField from "../../../../../common/form/Form";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import AutoSave from "../../../../../common/AutoSave/AutoSave";
import { AUTOSAVE_SUCCESS_MESSAGE } from "../../../Mrv/Constants";


const FeuD5QuaysideAndAnchorage = forwardRef(({ D5FeuQuaysideAndAnchorageData, refetchMonitoringData, dropdownData,vesselMstId}, ref) => {
    const { control, getValues, reset, formState } = useFormContext();
    const { isDirty } = formState;
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);

    useEffect(() => {
        if (D5FeuQuaysideAndAnchorageData) {
            reset(prev => ({ ...prev, d5: D5FeuQuaysideAndAnchorageData }));
        }
    }, [D5FeuQuaysideAndAnchorageData, reset]);

    AutoSave(ref, [], async () => {
        if (!isDirty) return;
        await handleSave(true);
    });

    const handleSave = async (isAuto = false) => {
        const formValues = getValues("d5");
        const payload = d5FeuFields.reduce((acc, { name }) => {
            acc[name] = formValues?.[name] ?? "";
            return acc;
        }, {});
        try {
            const FeuD5QuaysideAndAnchorage = await callApi({
                method: "POST",
                url: API_ROUTES.D5(vesselMstId),
                data: payload,
            });
            if (FeuD5QuaysideAndAnchorage?.isSuccess) {
                emitToasterMsg("success", "Table D.5", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, d5: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table D.5", FeuD5QuaysideAndAnchorage?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table D.5", error?.errorMessage);
        }
    };

    const monitoringMethodOptions = useMemo(() => {
        if (!dropdownData?.monitoringMethodOptions?.length) return [];

        return [
            { label: "Select method", value: "" },
            ...dropdownData.monitoringMethodOptions.map(({ name }) => ({
                label: name,
                value: name,
            })),
        ];
    }, [dropdownData]);

    const d5FeuFields = useMemo(() => {
        const base = FuelEuDCommonTableColumns.filter(
            f => f.name !== "backupMonitoringMethod" && f.name !== "methodDescription"
        );
        base.splice(1, 0, {
            label: "Description of method to treat data gaps",
            name: "methodDescription",
            maxLength: 4000,
        });

        return base;
    }, []);

    return (
        <div className="d5-section-form mt-4">
            <MDBRow className="mb-2">
                <MDBCol md="4">
                    <h6 className="titles-font">Title of method</h6>
                </MDBCol>
                <MDBCol md="6">
                    <h6 className="titles-font">
                        Method to treat data gaps regarding time spent at sea and time spent at berth (quayside and anchorage)
                    </h6>
                </MDBCol>
            </MDBRow>

            {d5FeuFields.map(({ label, name, maxLength, type }, index) => (
                <MDBRow className="align-items-center mb-3" key={index}>
                    <MDBCol md="4">
                        <Label name={label} bold />
                    </MDBCol>
                    <MDBCol md="4">
                        {type === "dropdown" ? (
                            <FormField
                                name={`d5.${name}`}
                                type="dropdown"
                                options={monitoringMethodOptions}
                            />
                        ) : (
                            <Controller
                                name={`d5.${name}`}
                                control={control}
                                render={({ field }) => (
                                    <TextArea
                                        id={`d5_${name}`}
                                        className="textarea-resize"
                                        value={field.value || ""}
                                        onChange={field.onChange}
                                        placeholder=""
                                        maxLength={maxLength}
                                        rows={1}
                                    />
                                )}
                            />
                        )}
                    </MDBCol>
                </MDBRow>
            ))}

            <div className="text-center">
                <Button id="feud5_save_btn"
                    disabled={!isDirty}
                    onClick={async () => {
                        manualSaveTriggered.current = true;
                        await handleSave(false);
                    }}>
                    Save
                </Button>
            </div>
        </div>
    );
});

export default FeuD5QuaysideAndAnchorage;
